/* Research Intelligence T3 — Evidence Inspector Modal.
 *
 * Operator-facing inspector for ONE row from the evidence panel
 * (either an `evidence_records` row OR a `user_confirmation_queue`
 * row). Shows full provenance + lets the operator approve / reject /
 * revoke (evidence) or approve / reject / edit-then-approve (queue).
 *
 * Strict T3 honesty rules (visible in UI):
 *   * Social-link rows clearly label "URL only — content not analyzed."
 *   * Source URL is hyperlinked but opened with `rel=noopener` so it
 *     can't fingerprint the operator session.
 *   * Internal IDs (id, dedupe_key) are shown ONLY in the inspector,
 *     never in the panel — operator-facing visibility only.
 */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, ShieldCheck, ShieldX, Trash2, CheckCircle2, XCircle, Pencil } from "lucide-react";

const STATE_COLOR = {
    approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    pending:  "bg-amber-500/10 text-amber-300 border-amber-500/30",
    rejected: "bg-red-500/10 text-red-300 border-red-500/30",
    revoked:  "bg-zinc-700/20 text-zinc-400 border-zinc-700",
};

function fmt(v) {
    if (v == null) return "—";
    if (typeof v === "string") return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function isSocialLink(target) {
    if (!target) return false;
    if (target.kind === "queue") return target.row?.kind === "social_link";
    return (target.row?.predicate || "").startsWith("social.")
        || (target.row?.subject_kind || "") === "social_profile";
}

export default function EvidenceInspector({ target, onClose, onAction, projectId }) {
    const [busy, setBusy] = useState(false);
    const [queueAction, setQueueAction] = useState("approve");
    const [decisionNote, setDecisionNote] = useState("");

    if (!target) return null;
    const isEvidence = target.kind === "evidence";
    const isQueue = target.kind === "queue";
    const row = target.row || {};
    const social = isSocialLink(target);

    // Sprint A — A0 same-owner cross-project mutation guard.
    // Every PATCH body MUST include the active `project_id` so the
    // backend can verify `row.project_id == body.project_id`. The
    // backend treats `null` as standalone evidence; we pass the
    // parent panel's active projectId (preferred) or fall back to
    // the row's own project_id when the panel didn't pass one.
    const activeProjectId = (projectId !== undefined && projectId !== null)
        ? projectId
        : (row.project_id ?? null);

    const callApi = async (method, path, body) => {
        setBusy(true);
        try {
            const { data } = await api({ method, url: path, data: body });
            toast.success("Saved");
            onAction?.(data);
            onClose?.();
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Action failed");
        } finally { setBusy(false); }
    };

    const evidenceAction = (action) =>
        callApi("patch", `/research-intel/evidence/${row.id}/${action}`, {
            project_id: activeProjectId,
            reason: decisionNote || undefined,
        });

    const queueResolve = () => {
        const body = { project_id: activeProjectId, action: queueAction };
        if (decisionNote) body.decision_note = decisionNote;
        if (queueAction === "edit_then_approve") {
            // Minimal edit: operator can edit candidate_payload via a JSON
            // textarea. Defensive parse so a malformed JSON shows a clear
            // toast instead of crashing the modal.
            try {
                body.edits = JSON.parse(decisionNote || "{}");
            } catch {
                toast.error("Edits must be valid JSON");
                return;
            }
        }
        return callApi("patch", `/research-intel/queue/${row.id}/resolve`, body);
    };

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose?.(); }}>
            <DialogContent
                className="max-w-2xl bg-zinc-950 border border-zinc-800 text-zinc-100"
                data-testid="evidence-inspector-modal"
            >
                <DialogHeader>
                    <DialogTitle className="font-mono text-sm uppercase tracking-wider text-zinc-300">
                        {isEvidence ? "Evidence Inspector" : "Queue Inspector"}
                    </DialogTitle>
                </DialogHeader>

                {/* ============ Provenance Block ============ */}
                <div className="space-y-3 text-xs">
                    {isEvidence ? (
                        <>
                            <Row label="Predicate" value={row.predicate} testid="ev-predicate" />
                            <Row label="Subject" value={`${row.subject_kind} / ${row.subject_id}`} testid="ev-subject" />
                            <Row label="Value" value={fmt(row.object_value)} pre testid="ev-value" />
                            <Row label="Source kind" value={row.source_kind} testid="ev-source-kind" />
                            <Row label="Source location" value={row.source_location} testid="ev-source-location" />
                            <Row label="Extractor" value={row.extractor} testid="ev-extractor" />
                            <Row label="Confidence" value={(row.confidence ?? 0).toFixed(2)} testid="ev-confidence" />
                            <Row label="Occurrences" value={row.occurrences ?? 1} testid="ev-occurrences" />
                            {row.raw_excerpt ? (
                                <Row label="Raw excerpt" value={row.raw_excerpt} pre testid="ev-raw-excerpt" />
                            ) : null}
                            <Row label="Fetched at" value={row.fetched_at} testid="ev-fetched-at" />
                            <div className="flex items-center gap-2 pt-1">
                                <span className="text-zinc-500">State:</span>
                                <Badge className={"font-mono text-[10px] " + (STATE_COLOR[row.approval_state] || "")}
                                       data-testid="ev-state-badge">
                                    {row.approval_state || "pending"}
                                </Badge>
                            </div>
                        </>
                    ) : (
                        <>
                            <Row label="Kind" value={row.kind} testid="qq-kind" />
                            <Row label="Suggested action" value={row.suggested_action} testid="qq-suggested-action" />
                            <Row label="Source summary" value={row.source_summary} testid="qq-source-summary" />
                            <Row label="Candidate payload" value={fmt(row.candidate_payload)} pre testid="qq-payload" />
                            <Row label="Linked evidence IDs"
                                 value={(row.linked_evidence_ids || []).join(", ") || "—"}
                                 testid="qq-linked-ids" />
                            <Row label="Expires at" value={row.expires_at} testid="qq-expires" />
                            <div className="flex items-center gap-2 pt-1">
                                <span className="text-zinc-500">Status:</span>
                                <Badge className="font-mono text-[10px] bg-amber-500/10 text-amber-300 border-amber-500/30"
                                       data-testid="qq-status-badge">
                                    {row.status || "pending"}
                                </Badge>
                            </div>
                        </>
                    )}
                    {row.source_url ? (
                        <a href={row.source_url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 underline-offset-4 hover:underline"
                           data-testid="inspector-source-url-link">
                            <ExternalLink className="w-3 h-3" />
                            {row.source_url.length > 64 ? row.source_url.slice(0, 64) + "…" : row.source_url}
                        </a>
                    ) : null}
                </div>

                {/* ============ Social Honesty Banner ============ */}
                {social ? (
                    <div className="mt-3 p-2 border border-amber-500/40 bg-amber-500/5 rounded-sm text-[11px] text-amber-200"
                         data-testid="social-honesty-banner">
                        <span className="font-mono uppercase tracking-wider text-amber-300">URL only — content not analyzed.</span>
                        <div className="text-amber-200/70 mt-0.5">
                            No posts, reels, hashtags or engagement metrics were fetched.
                            Confirming a social row here only updates the evidence record state.
                            To mark the underlying social profile as confirmed, use the
                            social-link confirm flow in the Research panel.
                        </div>
                    </div>
                ) : null}

                {/* ============ Inputs ============ */}
                {isQueue ? (
                    <div className="mt-3 space-y-2">
                        <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            Action
                        </label>
                        <select
                            value={queueAction}
                            onChange={(e) => setQueueAction(e.target.value)}
                            data-testid="qq-action-select"
                            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs rounded-sm px-2 py-1.5"
                        >
                            <option value="approve">Approve</option>
                            <option value="reject">Reject</option>
                            <option value="edit_then_approve">Edit then approve</option>
                        </select>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            {queueAction === "edit_then_approve"
                                ? "Edits (JSON merged into candidate_payload)"
                                : "Decision note (optional)"}
                        </label>
                        <textarea
                            rows={queueAction === "edit_then_approve" ? 4 : 2}
                            value={decisionNote}
                            onChange={(e) => setDecisionNote(e.target.value)}
                            placeholder={queueAction === "edit_then_approve" ? '{"key": "value"}' : "Why are you approving / rejecting?"}
                            data-testid="qq-decision-note"
                            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs rounded-sm px-2 py-1.5 font-mono"
                        />
                    </div>
                ) : (
                    <div className="mt-3 space-y-2">
                        <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            Note / reason (optional)
                        </label>
                        <textarea
                            rows={2}
                            value={decisionNote}
                            onChange={(e) => setDecisionNote(e.target.value)}
                            placeholder="Why are you approving / rejecting / revoking?"
                            data-testid="ev-decision-note"
                            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs rounded-sm px-2 py-1.5"
                        />
                    </div>
                )}

                {/* ============ Actions ============ */}
                <DialogFooter className="mt-4 gap-2 flex-wrap">
                    {isEvidence ? (
                        <>
                            <button type="button" disabled={busy}
                                    onClick={() => evidenceAction("approve")}
                                    data-testid="ev-approve-btn"
                                    className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 rounded-sm disabled:opacity-40 transition-colors inline-flex items-center gap-1">
                                <ShieldCheck className="w-3 h-3" /> Approve
                            </button>
                            <button type="button" disabled={busy}
                                    onClick={() => evidenceAction("reject")}
                                    data-testid="ev-reject-btn"
                                    className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-red-500/30 text-red-300 hover:bg-red-500/10 rounded-sm disabled:opacity-40 transition-colors inline-flex items-center gap-1">
                                <ShieldX className="w-3 h-3" /> Reject
                            </button>
                            <button type="button" disabled={busy}
                                    onClick={() => evidenceAction("revoke")}
                                    data-testid="ev-revoke-btn"
                                    className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-zinc-700 text-zinc-400 hover:bg-zinc-900 rounded-sm disabled:opacity-40 transition-colors inline-flex items-center gap-1">
                                <Trash2 className="w-3 h-3" /> Revoke
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" disabled={busy}
                                    onClick={queueResolve}
                                    data-testid="qq-resolve-btn"
                                    className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 rounded-sm disabled:opacity-40 transition-colors inline-flex items-center gap-1">
                                {queueAction === "approve" ? <CheckCircle2 className="w-3 h-3" /> :
                                 queueAction === "reject"  ? <XCircle className="w-3 h-3" /> :
                                                              <Pencil className="w-3 h-3" />}
                                Apply
                            </button>
                        </>
                    )}
                    <button type="button" disabled={busy}
                            onClick={() => onClose?.()}
                            data-testid="inspector-close-btn"
                            className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-zinc-700 text-zinc-400 hover:text-zinc-200 rounded-sm transition-colors">
                        Close
                    </button>
                </DialogFooter>

                {/* ============ Internal IDs (audit-only) ============ */}
                <div className="mt-4 pt-3 border-t border-zinc-800 text-[10px] font-mono text-zinc-600 space-y-0.5">
                    <div data-testid="inspector-internal-id">id: {row.id}</div>
                    {row.dedupe_key ? (
                        <div data-testid="inspector-dedupe-key">
                            dedupe_key: {row.dedupe_key.slice(0, 16)}…
                        </div>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, value, pre = false, testid }) {
    return (
        <div className="flex items-start gap-3" data-testid={testid}>
            <div className="w-32 shrink-0 text-[10px] font-mono uppercase tracking-wider text-zinc-500 pt-0.5">
                {label}
            </div>
            <div className={"flex-1 text-xs text-zinc-200 " + (pre ? "whitespace-pre-wrap font-mono break-all" : "break-words")}>
                {value ?? "—"}
            </div>
        </div>
    );
}
